import { forwardRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import MuiCheckbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { labels } from '@srichaitra/constants';

/**
 * Form controls, all of them Material UI's TextField.
 *
 * TextField already assembles the label, the input, the helper text and the
 * aria-describedby that ties them together — so this library's job is only to
 * translate the application's prop vocabulary (`invalid`, `error`, `hint`) onto
 * MUI's (`error`, `helperText`). Doing that translation in one place is what
 * makes every field in the app announce itself the same way.
 *
 * MUI shows one helper line, so an error replaces the hint while it is present.
 * That is the right precedence: what is wrong now matters more than general
 * guidance, and stacking both would move the layout on every keystroke.
 */

function helperFor({ error, hint }) {
  return error || hint || undefined;
}

export const Input = forwardRef(function Input(
  { id, label, type = 'text', invalid = false, error, hint, required, describedBy, ...rest },
  ref
) {
  return (
    <TextField
      inputRef={ref}
      id={id}
      label={label}
      type={type}
      required={required}
      error={invalid}
      helperText={helperFor({ error, hint })}
      slotProps={{ htmlInput: { 'aria-describedby': describedBy } }}
      {...rest}
    />
  );
});

/**
 * A password field with a reveal toggle.
 *
 * The toggle is a real button so it is reachable by keyboard, and its label
 * changes with the state so a screen reader announces what pressing it will do
 * now rather than what it did last time. `edge="end"` keeps it inside the
 * field's padding instead of widening the control.
 */
export const PasswordInput = forwardRef(function PasswordInput(
  { id, label, invalid = false, error, hint, required, describedBy, ...rest },
  ref
) {
  const [revealed, setRevealed] = useState(false);
  const toggleLabel = revealed ? labels.login.hidePassword : labels.login.showPassword;

  return (
    <TextField
      inputRef={ref}
      id={id}
      label={label}
      type={revealed ? 'text' : 'password'}
      required={required}
      error={invalid}
      helperText={helperFor({ error, hint })}
      slotProps={{
        htmlInput: { 'aria-describedby': describedBy },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setRevealed((current) => !current)}
                aria-label={toggleLabel}
                aria-pressed={revealed}
                title={toggleLabel}
                edge="end"
              >
                {revealed ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }
      }}
      {...rest}
    />
  );
});

export const TextArea = forwardRef(function TextArea(
  { id, label, rows = 4, invalid = false, error, hint, required, describedBy, ...rest },
  ref
) {
  return (
    <TextField
      inputRef={ref}
      id={id}
      label={label}
      multiline
      rows={rows}
      required={required}
      error={invalid}
      helperText={helperFor({ error, hint })}
      slotProps={{ htmlInput: { 'aria-describedby': describedBy } }}
      {...rest}
    />
  );
});

/**
 * A select built from an `options` array of { value, label }.
 *
 * Options are data rather than children, so a caller cannot hand it arbitrary
 * markup — the rule that keeps raw menu items out of pages.
 */
export const Select = forwardRef(function Select(
  { id, label, options = [], placeholder, invalid = false, error, hint, required, describedBy, ...rest },
  ref
) {
  return (
    <TextField
      inputRef={ref}
      id={id}
      label={label}
      select
      required={required}
      error={invalid}
      helperText={helperFor({ error, hint })}
      slotProps={{ htmlInput: { 'aria-describedby': describedBy } }}
      {...rest}
    >
      {placeholder ? (
        <MenuItem value="" disabled>
          {placeholder}
        </MenuItem>
      ) : null}
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
});

/**
 * A checkbox with its own label.
 *
 * Unlike the other controls this one owns its label, because a checkbox's label
 * sits beside it rather than above, and clicking the text must toggle the box —
 * which is what FormControlLabel wires up.
 */
export const Checkbox = forwardRef(function Checkbox({ id, label, describedBy, ...rest }, ref) {
  return (
    <FormControlLabel
      control={<MuiCheckbox inputRef={ref} id={id} inputProps={{ 'aria-describedby': describedBy }} {...rest} />}
      label={label}
    />
  );
});
